import { ampFetch } from './api';
import { filter, find, has, isEmpty, map, parseInt, reduce } from 'lodash';
import { isLoggedIn } from '~/account/utilities/helpers';
import { sumAddOnPricesExcludingBells } from '~/utilities/bells';

export const updateItemWithInvoice = (data, item, refreshKey) => {
    if (!has(data, 'invoice')) {
        console.error(`No invoice found for ${data}`);
        return item;
    }

    const invoice = data.invoice;

    return {
        ...item,
        refreshKey,
        tax: invoice.tax,
        invoice: {
            ...invoice,
            tax_rate: parseFloat(invoice.tax_rate) || 0
        },
        metadata: {
            amount_due: data.amount_due,
            mobile_app_discount: data.mobile_app_discount
        }
    };
};

export const updateItemWithDiscount = (discountInvoice, code, item) => {
    const discountTotal =
        discountInvoice.total === 0
            ? item.price
            : item.invoice.subtotal -
              (discountInvoice.total - discountInvoice.tax);
    const discount = discountInvoice.discount ?? discountTotal;

    return {
        ...item,
        tax: discountInvoice.tax,
        discount,
        invoice: {
            ...discountInvoice,
            subtotal: item.invoice.subtotal,
            discount,
            checkout_id: item.invoice.checkout_id
        },
        promoCode: {
            code,
            id: discountInvoice.stripe_invoice_id
        }
    };
};

export const deleteInvoice = async (invoice_id, accountId) => {
    return await ampFetch({
        route: 'api-user/invoices/delete-draft',
        options: {
            method: 'POST',
            body: JSON.stringify({ invoice_id, 'Amp-Account-Id': accountId })
        }
    });
};

const washes = {
    deleteInvoice: async (invoiceId, garage) => {
        deleteInvoice(invoiceId, garage).catch((e) => {
            console.error(e.message);
        });
    },
    deleteInvoiceById: (id, cartItems) => {
        const item = find(cartItems, {
            cartItemId: id,
            productType: 'digital'
        });
        const invoiceId = item?.invoice?.stripe_invoice_id;
        const garage = item?.vehicle?.garage;

        if (item && invoiceId && garage) {
            deleteInvoice(invoiceId, garage).catch((e) => {
                console.error(e.message);
            });
        }
    },
    getRemoteWashes: async (paging) => {
        const response = await ampFetch({
            route: 'api-user/washes/get-remote-washes',
            options: {
                method: 'POST',
                body: JSON.stringify(paging)
            }
        });
        if (response.data) {
            return {
                ...response,
                data: {
                    data: response.data.results,
                    has_more: response.data.next !== null
                }
            };
        }
        return response;
    },
    addPromoCode: async (code, locationId, item) => {
        const response = await ampFetch({
            route: 'api-user/invoices/add-promo-code-to-wash',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    'Amp-Account-Id': item?.vehicle?.garage,
                    'promo_code': code,
                    'invoice_id': item?.invoice?.stripe_invoice_id,
                    'wash_type_id': item?.serviceId,
                    'add_on_ids': map(item?.addOns, (addOn) =>
                        parseInt(addOn.serviceId)
                    ),
                    'location_id': locationId,
                    'vehicle_id': parseInt(item?.vehicle?.vehicleId)
                })
            }
        });

        if (response.success) {
            const newItem = updateItemWithDiscount(response.data, code, item);
            return { value: newItem, success: true };
        }

        return { value: item, success: false };
    },
    addInvoice: async (locationId, item, promotionCode = null) => {
        const nonBellsAddOns = filter(item.addOns, (addOn) => !addOn.useBells);
        const digitalTotal =
            (item.price + sumAddOnPricesExcludingBells(item.addOns)) *
            item.quantity;

        if (!has(item, 'vehicle') || !isLoggedIn()) {
            return { success: false, digitalItem: item, digitalTotal };
        }

        const accountId = item?.vehicle?.garage;
        const washTypeId = item?.serviceId;
        const vehicleId = item?.vehicle?.vehicleId;
        const code = promotionCode || item?.promoCode?.code;
        const addonsId = isEmpty(nonBellsAddOns)
            ? 'NO_ADDONS'
            : reduce(
                  nonBellsAddOns,
                  (acc, addon) => {
                      return acc + addon.serviceId;
                  },
                  ''
              );

        const refreshKey = `${accountId}-${washTypeId}-${vehicleId}-${locationId}-${addonsId}-${code || 'NO_CODE'}`;

        if (item.refreshKey === refreshKey && has(item, 'invoice')) {
            return {
                success: true,
                digitalItem: item,
                digitalTotal: item.invoice.total
            };
        }

        const response = await ampFetch({
            route: 'api-user/washes/preview-digital-wash',
            options: {
                method: 'POST',
                body: JSON.stringify({
                    wash_type_id: washTypeId,
                    location_id: locationId,
                    vehicle_id: parseInt(vehicleId),
                    price: item.price,
                    add_ons: map(item?.addOns, (addOn) => ({
                        id: parseInt(addOn.serviceId),
                        price: addOn.price,
                        ...(addOn.useBells && {
                            bells_redeemed: addOn.bellsCost
                        })
                    })),
                    ...(code && { promo_code: code })
                })
            }
        });

        if (response.success) {
            const digitalItem = updateItemWithInvoice(
                response.data,
                item,
                refreshKey
            );

            const hasInvoice = has(digitalItem, 'invoice');
            const finalTotal = hasInvoice
                ? digitalItem.invoice.total
                : digitalTotal;

            return {
                digitalItem,
                success: hasInvoice,
                digitalTotal: finalTotal
            };
        }

        return { success: false, digitalItem: item, digitalTotal };
    }
};

export default washes;
